/* Chat libre de l'élève, mené depuis le compte du parent/tuteur. Naïa répond à */
/* n'importe quelle question ; la conversation est lisible par le responsable, */
/* et l'enfant en est averti au-dessus du champ de saisie comme dans le prompt. */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from '../lib/routing';
import {
  HeaderContent,
  HEADER_TITLE_CLASS,
  APP_HEADER_HEIGHT_VAR,
} from '../components/shell/app-header';
import { Alert } from '@/components/ui/alert';
import { RetryAlert, SlowNotice } from '../components/chat-turn-notices';
import { newClientSendId, planRetry, textOfStoredTurn } from '../lib/turn-recovery';
import { SseSilenceError } from '../lib/sse';
import { Button } from '@/components/ui/button';
import {
  ArrowRight,
  CircleNotch,
  ClockCounterClockwise,
  Eye,
  FilePdf,
  Image as ImageIcon,
  Microphone,
  MicrophoneSlash,
  PaperPlaneTilt,
  Plus,
  Student,
} from '@phosphor-icons/react';
import Markdown from '../components/markdown';
import TypingDots from '../components/typing-dots';
import WidgetFrame from '../components/widget-frame';
import { StudentChatHistory } from '../components/student-chat-history';
import { api } from '../lib/api';
import {
  streamStudentChatTurn,
  StudentChatDailyCapError,
  StudentChatDisabledError,
  toolStatusLabel,
  type StudentChatWidget,
} from '../lib/student-chat';
import { uploadAttachment, type ChatAttachment } from '../lib/chat-api';
import { CHAT_SILENCE_ERROR, chatAttachmentErrorMessage } from '../lib/chat-errors';
import { useChatAttachments } from './chat/use-chat-attachments';
import { useSendLock } from './chat/use-send-lock';
import { PendingThumb } from './chat/pending-thumb';
import { IMAGE_MIMES, isImageMime } from './chat/attachments';
import { AttachmentList } from './chat/attachment-list';
import { WebVoiceSession, type VoiceState } from '../lib/voice-session';
import { formatSpeechTime, VOICE_REFUSAL_MESSAGE, type VoiceRefusal } from '../lib/student-voice';
import { cn } from '@/lib/utils';
import { composerKeyHandler } from '../lib/composer-keys';

/* Portée resserrée à Ismael (urgence P1) : image ET PDF, pas les formats
   Office — le bucket les accepte mais aucune extraction de texte server-side
   n'existe pour eux (voir attachments.ts côté API). */
const PDF_MIME = 'application/pdf';

interface Access {
  classroom_student_id: string;
  first_name: string;
  level: string | null;
  enabled: boolean;
  daily_turn_cap: number;
  turns_used_today: number;
  voice_daily_seconds: number;
  voice_seconds_used_today: number;
}

interface Thread {
  id: string;
  title: string;
  last_message_at: string;
}

interface Msg {
  role: 'user' | 'assistant';
  text: string;
  widgets?: StudentChatWidget[];
  attachments?: ChatAttachment[];
  incomplete?: boolean;
}

interface StoredMessage {
  turn_failed?: boolean;
  role: string;
  transcript: string;
  metadata: unknown;
  attachments?: ChatAttachment[] | null;
}

/* Meme gabarit que le fil enseignant : la reponse de Naïa se lit comme un */
/* document (pleine largeur, Markdown), la question de l'enfant reste une */
/* bulle compacte a droite. */
const HISTORY_WINDOW = 200;

const COLUMN = 'w-full max-w-[880px] mx-auto px-[max(16px,env(safe-area-inset-left))] sm:px-6';

const SUGGESTIONS = [
  'Explique-moi les fractions avec un exemple',
  'Pourquoi le ciel est bleu ?',
  "Comment on accorde le participe passé ?",
  'Aide-moi à réviser mes tables de multiplication',
];

/* Les widgets rejoués depuis la base vivent dans la trace d'outils du message. */
function widgetsOf(metadata: unknown): StudentChatWidget[] {
  const tools = (metadata as { tools?: Array<Record<string, unknown>> } | null)?.tools;
  if (!Array.isArray(tools)) return [];
  return tools
    .filter((t) => t.name === 'render_widget')
    .map((t) => {
      const args = (t.args ?? {}) as Record<string, unknown>;
      return {
        name: t.name as StudentChatWidget['name'],
        toolCallId: String(t.toolCallId ?? ''),
        html: String(args.html ?? ''),
        title: (args.title as string) ?? null,
        args,
      };
    });
}

export default function StudentChat() {
  const { studentId } = useParams<{ studentId: string }>();
  const [searchParams] = useSearchParams();
  const wantedThreadId = searchParams.get('thread');
  const navigate = useNavigate();
  const [access, setAccess] = useState<Access | null>(null);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const guardSend = useSendLock();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [turnsLeft, setTurnsLeft] = useState<number | null>(null);
  const [toolStatus, setToolStatus] = useState<string | null>(null);
  const [slow, setSlow] = useState(false);
  const [retryable, setRetryable] = useState(false);
  const failedTurnRef = useRef<{ tid: string; text: string; uploaded: ChatAttachment[]; knownBefore: number; clientSendId: string } | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [voiceState, setVoiceState] = useState<VoiceState>('idle');
  const [voiceNotice, setVoiceNotice] = useState<string | null>(null);
  const [voiceLive, setVoiceLive] = useState<{ user: string; assistant: string }>({ user: '', assistant: '' });
  const voiceRef = useRef<WebVoiceSession | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);
  const { pending, pickFiles, removePending, clearPending } = useChatAttachments({
    disabled: busy,
    onError: setError,
  });

  const [threads, setThreads] = useState<Thread[]>([]);
  const [isWide, setIsWide] = useState(
    () => typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(min-width: 900px)').matches,
  );
  const [historyOpen, setHistoryOpen] = useState(false);
  const historyPanelRef = useRef<HTMLDivElement>(null);
  const historyWasOpen = useRef(false);
  useEffect(() => {
    if (historyOpen) {
      historyWasOpen.current = true;
      historyPanelRef.current?.focus();
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') setHistoryOpen(false);
      };
      window.addEventListener('keydown', onKey);
      return () => window.removeEventListener('keydown', onKey);
    }
    if (historyWasOpen.current) {
      historyWasOpen.current = false;
      document.getElementById('student-chat-history-toggle')?.focus();
    }
  }, [historyOpen]);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(min-width: 900px)');
    const onChange = (e: MediaQueryListEvent) => setIsWide(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const loadThreads = useCallback(async (sid: string): Promise<Thread[]> => {
    try {
      const list = (await api.parentOrTutor.student.chatThreads.query({ studentId: sid })) as Thread[];
      setThreads(list);
      return list;
    } catch {
      return [];
    }
  }, []);

  /* `?thread=` ouvre un fil précis, choisi depuis la liste des conversations ;
     sans lui on reprend le plus récent. La procédure vérifie l'appartenance du
     fil à CET enfant, donc un id étranger dans l'URL est refusé côté serveur. */
  const openThread = useCallback(async (sid: string, wanted: string | null) => {
    const list = await loadThreads(sid);
    const chosen = (wanted && list.find((t) => t.id === wanted)) || list[0];
    if (!chosen) {
      setThreadId(null);
      setMessages([]);
      return;
    }
    setThreadId(chosen.id);
    const stored = (await api.parentOrTutor.student.chatMessages.query({
      studentId: sid,
      threadId: chosen.id,
    })) as StoredMessage[];
    setMessages(
      stored.map((m) => ({
        role: m.role === 'assistant' ? ('assistant' as const) : ('user' as const),
        text: textOfStoredTurn(m.transcript, m.turn_failed),
        widgets: widgetsOf(m.metadata),
        attachments: m.attachments?.length ? m.attachments : undefined,
      })),
    );
  }, [loadThreads]);

  const selectThread = (id: string) => {
    if (!studentId) return;
    if (id !== threadId) void openThread(studentId, id);
    if (!isWide) setHistoryOpen(false);
  };

  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    api.parentOrTutor.student.chatAccess
      .query({ studentId })
      .then(async (a) => {
        if (cancelled) return;
        const acc = a as Access;
        setAccess(acc);
        setTurnsLeft(acc.daily_turn_cap - acc.turns_used_today);
        if (acc.enabled) await openThread(studentId, wantedThreadId);
      })
      .catch(() => !cancelled && setLoadError('La conversation n\'a pas pu être chargée.'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [studentId, openThread, wantedThreadId, reloadKey]);

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy]);

  /* Le champ grandit avec le brouillon : sans cela l'enfant tape a l'aveugle */
  /* dans une fente d'une ligne des que sa question depasse la largeur. */
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 140)}px`;
  }, [draft]);

  const newThread = async () => {
    if (!studentId) return;
    setError(null);
    try {
      const t = (await api.parentOrTutor.student.createChatThread.mutate({ studentId })) as Thread;
      setThreadId(t.id);
      setMessages([]);
      setThreads((prev) => [t, ...prev]);
      if (!isWide) setHistoryOpen(false);
    } catch {
      setError("La nouvelle conversation n'a pas pu être créée. Réessaie dans un instant.");
    }
  };

  /* `busy` monte AVANT la creation du fil et le televersement, pas apres :
     sinon le bouton reste actif pendant tout l'envoi du fichier, et un second
     geste genere — et facture — une seconde reponse. Le verrou de useSendLock
     couvre en plus les gestes d'un meme tick, que ce drapeau d'etat manque. */
  const send = (raw?: string) => {
    const text = (raw ?? draft).trim();
    if ((!text && pending.length === 0) || !studentId || busy) return;
    guardSend(() => sendTurn(text));
  };

  const sendTurn = async (text: string) => {
    setError(null);
    setBusy(true);
    try {
      let tid = threadId;
      if (!tid) {
        try {
          const t = (await api.parentOrTutor.student.createChatThread.mutate({ studentId })) as Thread;
          tid = t.id;
          setThreadId(t.id);
        } catch {
          setError("La conversation n'a pas pu démarrer. Réessaie dans un instant.");
          return;
        }
      }

      /* Upload sequentiel : une piece jointe en echec doit rester visible dans */
      /* l'erreur plutot que de disparaitre silencieusement du tour. */
      const uploaded: ChatAttachment[] = [];
      for (const p of pending) {
        try {
          uploaded.push(await uploadAttachment({ threadId: tid, file: p.file }));
        } catch (err) {
          setError(chatAttachmentErrorMessage(err));
          return;
        }
      }
      const onlyImages = uploaded.length > 0 && uploaded.every((a) => isImageMime(a.mime));
      const attachFallback = onlyImages ? 'Regarde cette image.' : 'Regarde ce document.';
      const sentText = text || attachFallback;

      setDraft('');
      clearPending();
      const clientSendId = newClientSendId();
      failedTurnRef.current = { tid, text: sentText, uploaded, knownBefore: messages.length, clientSendId };
      setMessages((m) => [
        ...m,
        { role: 'user', text: sentText, attachments: uploaded.length ? uploaded : undefined },
        { role: 'assistant', text: '' },
      ]);
      await runStream(tid, sentText, uploaded, text, clientSendId);
    } finally {
      setBusy(false);
      setToolStatus(null);
      void loadThreads(studentId);
    }
  };

  const runStream = async (tid: string, sentText: string, uploaded: ChatAttachment[], text: string, clientSendId: string) => {
    setError(null);
    setRetryable(false);
    setSlow(false);
    setToolStatus(null);
    const appendToLast = (fn: (last: Msg) => Msg) =>
      setMessages((m) => {
        const copy = m.slice();
        const last = copy[copy.length - 1];
        if (last && last.role === 'assistant') copy[copy.length - 1] = fn(last);
        return copy;
      });
    const markIncomplete = () =>
      setMessages((m) => {
        const last = m[m.length - 1];
        if (!last || last.role !== 'assistant') return m;
        if (!last.text && !last.widgets?.length) return m.slice(0, -1);
        return [...m.slice(0, -1), { ...last, incomplete: true }];
      });
    const rollback = () => {
      setMessages((m) => m.slice(0, -2));
      setDraft((d) => (d ? d : text));
    };

    try {
      await streamStudentChatTurn(
        studentId,
        tid,
        sentText,
        {
          onMeta: (meta) => setTurnsLeft(meta.turnsLeft),
          onDelta: (t) => {
            setToolStatus(null);
            appendToLast((last) => ({ ...last, text: last.text + t }));
          },
          onTool: (name) => setToolStatus(toolStatusLabel(name)),
          onWidget: (w) => appendToLast((last) => ({ ...last, widgets: [...(last.widgets ?? []), w] })),
          onRetract: () => appendToLast((last) => ({ ...last, text: '' })),
          onDone: (full) => appendToLast((last) => (last.text ? last : { ...last, text: full })),
          onError: (msg) => { setError(msg); markIncomplete(); setRetryable(true); },
          onSlow: () => setSlow(true),
          onActive: () => setSlow(false),
        },
        undefined,
        uploaded,
        clientSendId,
      );
    } catch (e) {
      if (e instanceof StudentChatDailyCapError) {
        setError(`Tu as atteint la limite de ${e.cap} messages pour aujourd'hui. On se reparle demain !`);
        setTurnsLeft(0);
        rollback();
        setDraft('');
      } else if (e instanceof StudentChatDisabledError) {
        setError("Le chat n'est plus activé pour ce compte. Parles-en à ton parent.");
        setAccess((a) => (a ? { ...a, enabled: false } : a));
      } else {
        setError(e instanceof SseSilenceError
          ? CHAT_SILENCE_ERROR
          : "Naïa n'a pas pu répondre. Vérifie ta connexion et réessaie.");
        markIncomplete();
        setRetryable(true);
      }
    } finally {
      setSlow(false);
    }
  };

  const retryTurn = () => {
    const failed = failedTurnRef.current;
    if (!failed || busy) return;
    guardSend(async () => {
      setError(null);
      setRetryable(false);
      setBusy(true);
      try {
        const load = async () => (await api.parentOrTutor.student.chatMessages.query({ studentId, threadId: failed.tid })) as StoredMessage[];
        const plan = await planRetry(
          async () => (await load()).map((m) => ({ role: m.role === 'assistant' ? 'assistant' : 'user', transcript: m.transcript, failed: m.turn_failed, clientSendId: (m.metadata as { clientSendId?: string } | null)?.clientSendId })),
          failed.knownBefore,
          failed.text,
          HISTORY_WINDOW,
          failed.clientSendId,
        );
        if (plan === 'already-processed') {
          const stored = await load();
          setMessages(stored.map((m) => ({
            role: m.role === 'assistant' ? ('assistant' as const) : ('user' as const),
            text: textOfStoredTurn(m.transcript, m.turn_failed),
            widgets: widgetsOf(m.metadata),
            attachments: m.attachments?.length ? m.attachments : undefined,
          })));
          failedTurnRef.current = null;
          return;
        }
        setMessages((m) => {
          const last = m[m.length - 1];
          const base = last && last.role === 'assistant' ? m.slice(0, -1) : m;
          return [...base, { role: 'assistant', text: '' }];
        });
        await runStream(failed.tid, failed.text, failed.uploaded, failed.text, failed.clientSendId);
      } finally {
        setBusy(false);
        setToolStatus(null);
        void loadThreads(studentId);
      }
    });
  };

  const onKey = composerKeyHandler(() => void send());

  const stopVoice = useCallback(() => {
    voiceRef.current?.stop();
    voiceRef.current = null;
    setVoiceState('idle');
    setVoiceLive({ user: '', assistant: '' });
  }, []);

  /* Le micro se coupe si l'onglet passe en arrière-plan : appel entrant, écran
     verrouillé, changement d'application. Sans ça la session continue de
     consommer du budget pendant que l'enfant n'est plus là. */
  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState === 'hidden' && voiceRef.current) stopVoice();
    };
    document.addEventListener('visibilitychange', onHidden);
    return () => document.removeEventListener('visibilitychange', onHidden);
  }, [stopVoice]);

  useEffect(() => {
    return () => {
      voiceRef.current?.stop();
    };
  }, []);

  const startVoice = async () => {
    if (!studentId || !access) return;
    let tid = threadId;
    if (!tid) {
      try {
        const t = (await api.parentOrTutor.student.createChatThread.mutate({ studentId })) as Thread;
        tid = t.id;
        setThreadId(t.id);
        void loadThreads(studentId);
      } catch {
        setError("La conversation n'a pas pu démarrer. Réessaie dans un instant.");
        return;
      }
    }
    setVoiceNotice(null);
    setError(null);
    const session = new WebVoiceSession(
      {
        onState: setVoiceState,
        onTranscript: (text, role) =>
          setVoiceLive((v) => (role === 'user' ? { ...v, user: text } : { ...v, assistant: text })),
        onTurnComplete: ({ user, assistant }) => {
          setVoiceLive({ user: '', assistant: '' });
          setMessages((m) => [
            ...m,
            ...(user ? [{ role: 'user' as const, text: user }] : []),
            ...(assistant ? [{ role: 'assistant' as const, text: assistant }] : []),
          ]);
          void loadThreads(studentId);
        },
        onServerEvent: (ev) => {
          const kind = ev.event as string;
          const left = Number(ev.remainingSeconds ?? 0);
          if (kind === 'refused') {
            setVoiceNotice(VOICE_REFUSAL_MESSAGE[ev.reason as VoiceRefusal] ?? 'Le mode vocal est indisponible.');
            stopVoice();
          } else if (kind === 'warn') {
            setVoiceNotice(`Il te reste ${formatSpeechTime(left)} de parole aujourd'hui.`);
          } else if (kind === 'last_call') {
            setVoiceNotice(`Plus que ${formatSpeechTime(left)} — termine ta phrase, on continue par écrit ensuite.`);
          } else if (kind === 'stop') {
            setVoiceNotice("C'est fini pour la parole aujourd'hui. Tu peux continuer à écrire !");
            stopVoice();
          }
        },
        onError: () => {
          /* Permission micro refusée, matériel indisponible, réseau coupé : un
             seul message, actionnable, plutôt qu'une erreur technique. */
          setVoiceNotice("Je n'arrive pas à accéder au micro. Vérifie l'autorisation dans ton navigateur.");
          stopVoice();
        },
      },
      'student',
      tid,
      { studentId },
    );
    voiceRef.current = session;
    try {
      await session.start();
    } catch {
      setVoiceNotice("Je n'arrive pas à démarrer le micro.");
      stopVoice();
    }
  };

  const voiceOn = voiceState !== 'idle' && voiceState !== 'error';
  /* Le bouton n'apparaît que si le responsable a laissé du temps de parole :
     l'afficher pour un enfant à zéro seconde ne ferait qu'annoncer un refus. */
  const voiceAllowed = (access?.voice_daily_seconds ?? 0) > 0;


  const backTo = studentId ? `/tutor/enfant/${studentId}` : undefined;
  const capped = turnsLeft === 0;

  if (loading) {
    return (
      <div className="flex flex-col" style={{ height: `calc(100dvh - var(${APP_HEADER_HEIGHT_VAR}, 0px))` }}>
        <HeaderContent width="md" showBurger backTo={backTo} backLabel="Fiche">
          <h1 className={HEADER_TITLE_CLASS}>Discuter avec Naïa</h1>
        </HeaderContent>
        <p className="text-muted-foreground text-[13px] text-center my-[10px]">Chargement…</p>
      </div>
    );
  }

  if (loadError || !access) {
    return (
      <div className="flex flex-col" style={{ height: `calc(100dvh - var(${APP_HEADER_HEIGHT_VAR}, 0px))` }}>
        <HeaderContent width="md" showBurger backTo={backTo} backLabel="Fiche">
          <h1 className={HEADER_TITLE_CLASS}>Discuter avec Naïa</h1>
        </HeaderContent>
        <div className={cn(COLUMN, 'py-6')}>
          <p className="text-[14px] text-foreground font-semibold">
            {loadError ?? "La conversation n'a pas pu être chargée."}
          </p>
          <p className="text-[13px] text-muted-foreground mt-1">
            La connexion a peut-être été interrompue.
          </p>
          <Button className="mt-3" onClick={() => setReloadKey((k) => k + 1)}>
            Réessayer
          </Button>
        </div>
      </div>
    );
  }

  if (!access.enabled) {
    return (
      <div className="flex flex-col" style={{ height: `calc(100dvh - var(${APP_HEADER_HEIGHT_VAR}, 0px))` }}>
        <HeaderContent
          width="md"
          showBurger
          backTo={backTo}
          backLabel="Fiche"
          eyebrow={access.first_name}
          eyebrowIcon={<Student size={12} weight="bold" />}
        >
          <h1 className={HEADER_TITLE_CLASS}>Discuter avec Naïa</h1>
        </HeaderContent>
        <div className={cn(COLUMN, 'py-6')}>
          <p className="text-[14px] text-foreground font-semibold">Le chat n'est pas activé.</p>
          <p className="text-[13px] text-muted-foreground mt-1">
            Un parent / tuteur doit l'activer depuis la fiche de {access.first_name}.
          </p>
          {backTo && (
            <Button className="mt-3" onClick={() => navigate(backTo)}>
              Ouvrir la fiche de {access.first_name}
            </Button>
          )}
        </div>
      </div>
    );
  }

  const historyActions = (
    <>
      <Button
        variant="outline"
        size="sm"
        className="min-h-11 px-3"
        id="student-chat-history-toggle"
        onClick={() => setHistoryOpen((v) => !v)}
        aria-pressed={historyOpen}
        aria-label="Historique des conversations"
      >
        <ClockCounterClockwise size={15} />
        Historique
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="min-h-11 px-3"
        onClick={() => void newThread()}
        disabled={busy}
        aria-label="Nouvelle conversation"
      >
        <Plus size={15} />
        Nouvelle
      </Button>
    </>
  );

  const isEmpty = messages.length === 0 && !busy;

  return (
    <div
      className="flex flex-col overflow-hidden"
      style={{ height: `calc(100dvh - var(${APP_HEADER_HEIGHT_VAR}, 0px))` }}
    >
      <HeaderContent
        width="md"
        showBurger
        denseActions
        backTo={backTo}
        backLabel="Fiche"
        eyebrow={access.first_name}
        eyebrowIcon={<Student size={12} weight="bold" />}
        actions={isWide ? historyActions : undefined}
      >
        <h1 className={HEADER_TITLE_CLASS}>Discuter avec Naïa</h1>
      </HeaderContent>

      {!isWide && (
        <div className="flex items-center gap-2 shrink-0 px-4 py-2 border-b border-border">
          <Button
            variant="outline"
            size="sm"
            className="min-h-11 flex-1 gap-2"
            id="student-chat-history-toggle"
            onClick={() => setHistoryOpen((v) => !v)}
            aria-pressed={historyOpen}
            aria-label="Historique des conversations"
          >
            <ClockCounterClockwise size={16} />
            Historique
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="min-h-11 flex-1 gap-2"
            onClick={() => void newThread()}
            disabled={busy}
            aria-label="Nouvelle conversation"
          >
            <Plus size={16} />
            Nouvelle
          </Button>
        </div>
      )}

      <div className="flex-1 min-h-0 flex relative">
        {isWide && (
          <div className={cn('absolute inset-0 z-[950] flex justify-end overflow-hidden', !historyOpen && 'pointer-events-none')}>
            <div
              className={cn(
                'absolute inset-0 bg-[rgba(7,16,28,0.40)] cursor-pointer transition-opacity duration-[220ms] ease-[ease]',
                historyOpen ? 'opacity-100' : 'opacity-0',
              )}
              onClick={() => setHistoryOpen(false)}
              aria-hidden
            />
            <div
              ref={historyPanelRef}
              tabIndex={-1}
              role="dialog"
              aria-label="Historique des conversations"
              aria-hidden={!historyOpen}
              inert={!historyOpen}
              className={cn(
                'relative w-[380px] max-w-full h-full min-h-0 border-l border-border bg-card overflow-hidden outline-none transition-transform duration-[220ms] ease-[ease]',
                historyOpen ? 'translate-x-0 shadow-lg' : 'translate-x-[102%]',
              )}
            >
              <StudentChatHistory
                threads={threads}
                activeThreadId={threadId}
                onSelect={selectThread}
                onNew={() => void newThread()}
                onClose={() => setHistoryOpen(false)}
              />
            </div>
          </div>
        )}

        <div className="flex-1 min-w-0 min-h-0 flex flex-col">
          <div ref={scrollerRef} className="flex-1 min-h-0 overflow-y-auto py-3">
            <div className={cn(COLUMN, 'flex flex-col gap-3')}>
          {isEmpty && (
            <div className="flex flex-col gap-4 pt-2">
              <p className="text-[14px] text-muted-foreground leading-relaxed">
                Pose ta question à Naïa — sur l'école, ou sur ce que tu veux comprendre. Elle
                explique et fait chercher, elle ne fait pas les devoirs à ta place.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => void send(s)}
                    disabled={capped}
                    className="flex items-center justify-between gap-3 text-left px-4 py-3 min-h-11 rounded-card border border-border bg-card text-[13.5px] text-foreground hover:bg-accent hover:text-primary hover:border-primary/30 transition-colors cursor-pointer font-[inherit] leading-snug group disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span>{s}</span>
                    <ArrowRight size={14} className="shrink-0 opacity-30 group-hover:opacity-70 transition-opacity" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={cn('flex flex-col gap-2', m.role === 'user' ? 'items-end' : 'items-stretch')}>
              {(m.text || m.role === 'user') &&
                (m.role === 'user' ? (
                  <div className="max-w-[min(80%,520px)] bg-primary text-primary-foreground rounded-card rounded-br-[6px] p-[11px_14px] shadow-card text-[15px] leading-[1.45] whitespace-pre-wrap break-words">
                    <AttachmentList attachments={m.attachments} size="sm" tone="onPrimary" className="mb-2" />
                    {m.text}
                  </div>
                ) : (
                  <div className="max-w-full bg-naia-card-alt border border-border rounded-card p-[14px_18px] shadow-card text-[15.5px] leading-[1.6] text-foreground break-words">
                    <Markdown text={m.text} />
                    {m.incomplete && (
                      <p className="mt-2 text-[12.5px] text-muted-foreground">Réponse incomplète — la suite n'est pas arrivée.</p>
                    )}
                  </div>
                ))}
              {m.widgets?.map((w) => (
                <div key={w.toolCallId} className="w-full max-w-full">
                  <WidgetFrame html={w.html} title={w.title ?? undefined} />
                </div>
              ))}
            </div>
          ))}

          {voiceLive.user && (
            <div className="self-end max-w-[min(80%,520px)] bg-primary text-primary-foreground rounded-card rounded-br-[6px] p-[11px_14px] shadow-card text-[15px] leading-[1.45] whitespace-pre-wrap break-words italic opacity-90">
              {voiceLive.user}
            </div>
          )}
          {voiceLive.assistant && (
            <div className="max-w-full bg-naia-card-alt border border-border rounded-card p-[14px_18px] shadow-card text-[15.5px] leading-[1.6] text-foreground break-words italic opacity-90">
              {voiceLive.assistant}
            </div>
          )}
          {voiceState === 'connecting' && (
            <div className="flex flex-col gap-1.5 items-start">
              <TypingDots label="Je t'écoute dans un instant" />
            </div>
          )}

          {busy && messages[messages.length - 1]?.text === '' && (
            <div className="flex flex-col gap-1.5 items-start">
              <TypingDots label={toolStatus ?? 'Naïa réfléchit'} />
              {toolStatus && <span className="text-[12.5px] text-muted-foreground">{toolStatus}</span>}
            </div>
          )}
          {busy && slow && <SlowNotice text="Naïa met plus de temps que d'habitude. Patiente encore un peu, ça peut arriver." />}
        </div>
      </div>

      <div className="flex-none border-t border-border bg-card [padding:10px_0_max(10px,env(safe-area-inset-bottom))]">
        <div className={COLUMN}>
          {/* Fixe, hors de la zone de défilement des messages : un fil déjà long
              fait défiler cette alerte hors champ pendant que l'auto-scroll
              ramène l'enfant en bas, exactement là où elle resterait invisible
              (NAI-228 — plafond atteint, aucun message visible). */}
          {error && (retryable && !busy
            ? <RetryAlert message={error} onRetry={retryTurn} />
            : <Alert className="mb-2">{error}</Alert>)}
          {capped && (
            <p className="text-[12.5px] text-foreground font-semibold mb-2">
              Plus de messages aujourd'hui — à demain !
            </p>
          )}
          <div className="mb-2">
            <div className="flex items-center gap-3">
              {/* Dit à l'enfant ce que le prompt lui dit aussi : rien ici n'est privé. */}
              <p className="flex items-center gap-[6px] text-[12px] text-muted-foreground min-w-0">
                <Eye size={13} weight="bold" className="shrink-0" />
                Tes parents peuvent lire cette conversation.
              </p>
              {turnsLeft !== null && turnsLeft > 0 && turnsLeft <= 10 && (
                <span className="ml-auto shrink-0 text-[12px] font-bold text-muted-foreground whitespace-nowrap">
                  {turnsLeft} restants
                </span>
              )}
            </div>
            {/* Transparence IA : visible dans tous les états — au plafond l'enfant ne peut plus écrire mais lit encore des réponses générées par une IA. */}
            <p className="text-[11.5px] text-muted-foreground mt-1">Naïa est une IA : elle peut se tromper.</p>
          </div>
          {voiceNotice && <Alert className="mb-2">{voiceNotice}</Alert>}
          {pending.length > 0 && (
            <div className="flex gap-2 mb-2 flex-wrap">
              {pending.map((p) => (
                <PendingThumb key={p.id} att={p} onRemove={() => removePending(p.id)} />
              ))}
            </div>
          )}
          <div className="flex gap-2 items-end">
            <input
              ref={imageInputRef}
              type="file"
              accept={IMAGE_MIMES.join(',')}
              multiple
              onChange={pickFiles}
              className="hidden"
            />
            <input
              ref={docInputRef}
              type="file"
              accept={PDF_MIME}
              multiple
              onChange={pickFiles}
              className="hidden"
            />
            <button
              type="button"
              className="flex-none w-12 h-12 grid place-items-center border border-border rounded-card bg-background text-foreground cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => imageInputRef.current?.click()}
              disabled={busy || capped}
              aria-label="Ajouter une photo"
              title="Ajouter une photo"
            >
              <ImageIcon size={20} weight="bold" />
            </button>
            <button
              type="button"
              className="flex-none w-12 h-12 grid place-items-center border border-border rounded-card bg-background text-foreground cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => docInputRef.current?.click()}
              disabled={busy || capped}
              aria-label="Ajouter un PDF"
              title="Ajouter un PDF"
            >
              <FilePdf size={20} weight="bold" />
            </button>
            <textarea
              ref={textareaRef}
              className="flex-1 min-w-0 resize-none font-[inherit] text-[16px] leading-[1.4] p-[12px_14px] border border-border rounded-card bg-background text-foreground max-h-[140px] focus:outline-none focus:border-primary/60 disabled:opacity-60"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKey}
              rows={1}
              placeholder={capped ? 'Reviens demain…' : 'Ta question…'}
              disabled={busy || capped}
            />
            {voiceAllowed && (
              <button
                type="button"
                className={cn(
                  'flex-none w-12 h-12 grid place-items-center rounded-card cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed',
                  voiceOn
                    ? 'border-none bg-primary text-primary-foreground'
                    : 'border border-border bg-background text-foreground',
                )}
                onClick={() => (voiceOn ? stopVoice() : void startVoice())}
                disabled={busy}
                aria-pressed={voiceOn}
                aria-label={voiceOn ? 'Arrêter de parler' : 'Parler avec Naïa'}
              >
                {voiceOn ? <MicrophoneSlash size={20} weight="bold" /> : <Microphone size={20} weight="bold" />}
              </button>
            )}
            <button
              type="button"
              className="flex-none w-12 h-12 grid place-items-center border-none rounded-card bg-primary text-primary-foreground cursor-pointer disabled:bg-accent disabled:text-muted-foreground disabled:cursor-not-allowed"
              onClick={() => void send()}
              disabled={busy || (!draft.trim() && pending.length === 0) || capped}
              aria-label="Envoyer"
            >
              {busy ? <CircleNotch size={20} className="animate-spin" /> : <PaperPlaneTilt size={20} />}
            </button>
          </div>
        </div>
      </div>
        </div>
      </div>

      {!isWide && (
        <div className={cn('fixed inset-0 z-[950] flex justify-end', !historyOpen && 'pointer-events-none')}>
          <div
            className={cn(
              'absolute inset-0 bg-[rgba(7,16,28,0.40)] cursor-pointer transition-opacity duration-[220ms] ease-[ease]',
              historyOpen ? 'opacity-100' : 'opacity-0',
            )}
            onClick={() => setHistoryOpen(false)}
            aria-hidden
          />
          <div
            ref={historyPanelRef}
            tabIndex={-1}
            role="dialog"
            aria-label="Historique des conversations"
            aria-hidden={!historyOpen}
            inert={!historyOpen}
            className={cn(
              'relative w-[min(320px,86vw)] h-full min-h-0 outline-none bg-card border-l border-border pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] transition-transform duration-[220ms] ease-[ease]',
              historyOpen ? 'translate-x-0 shadow-lg' : 'translate-x-[102%]',
            )}
          >
            <StudentChatHistory
              threads={threads}
              activeThreadId={threadId}
              onSelect={selectThread}
              onNew={() => void newThread()}
              onClose={() => setHistoryOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
